import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, X, CheckCircle } from 'lucide-react';
import { getPlantSafetyWorkPermits, deletePlantSafetyWorkPermit } from '../../../services/plantSafetyWorkPermitService';
import Pagination from '../../../components/Pagination/Pagination';

interface WorkPermit {
  id: number;
  refNo: string;
  createdDate: string;
  department: string;
  priority: string;
  location: string;
  status: string;
  statusColor: string;
  createdBy: number | null;
}

interface PlantRegPermitTableProps {
  onEdit?: (id: number) => void;
}

const ITEMS_PER_PAGE = 10;

const PlantRegPermitTable = forwardRef<any, PlantRegPermitTableProps>(({ onEdit }, ref) => {
  const navigate = useNavigate();

  const [allData, setAllData] = useState<WorkPermit[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const currentUserID = Number(localStorage.getItem('currentUserID'));

  const totalPages = Math.max(1, Math.ceil(allData.length / ITEMS_PER_PAGE));

  const data = allData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => {
    fetchWorkPermits();
  }, []);

  const fetchWorkPermits = async (page = 1, search = '') => {
    try {
      const response = await getPlantSafetyWorkPermits(page, search);

      const items = Array.isArray(response) ? response : response?.data || [];

      const filtered = search
        ? items.filter((item: any) => Object.values(item).some((val) => String(val).toLowerCase().includes(search.toLowerCase())))
        : items;

      const mappedData: WorkPermit[] = filtered.map((item: any) => ({
        id: item.id,
        refNo: item.reference_number,
        createdDate: item.created_at,
        department: item.department,
        priority: item.priority,
        location: item.location,
        status: item.status,
        statusColor: getStatusTextColor(item.status),
        createdBy: item.created_by ?? null,
      }));

      setAllData(mappedData);
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to fetch plant safety work permits', error);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchWorkPermits,
  }));

  const getStatusTextColor = (status: string) => {
    if (status === 'New') return 'text-purple-600';
    if (status === 'Permit Accepted') return 'text-pink-600';
    if (status === 'Permit Issued') return 'text-fuchsia-500';
    if (status === 'Clearance Done (AO)') return 'text-green-600';
    return 'text-gray-500';
  };

  const getDotColor = (status: string) => {
    if (status === 'New') return 'bg-purple-600';
    if (status === 'Permit Accepted') return 'bg-pink-600';
    if (status === 'Permit Issued') return 'bg-fuchsia-500';
    if (status === 'Clearance Done (AO)') return 'bg-green-600';
    return 'bg-gray-400';
  };

  // ── Lock Edit/Delete for the Responsible Person only once the Appointed Operator
  // has completed 'Add Instructions' (status has moved past 'Instruction Pending').
  // Staying enabled through 'New' and 'Instruction Pending' covers the RP notifying
  // the AO — that alone should not lock the record. Re-enabled automatically if a
  // later rejection resets the permit back to 'New'. ──
  const canEditOrDelete = (item: WorkPermit) =>
    Number(item.createdBy) === currentUserID && (item.status === 'New' || item.status === 'Instruction Pending');

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId === null) return;

    setDeleteLoading(true);
    await deletePlantSafetyWorkPermit(deleteId);
    await fetchWorkPermits();
    setDeleteLoading(false);
    handleCancelDelete();

    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="p-4">
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full text-sm text-left">
            <thead className="text-gray-600 text-xs">
              <tr className="h-[45px]">
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Sl. No</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Ref No.</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Created Date</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Department</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Priority</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Location</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 border text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>

            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No records found.
                  </td>
                </tr>
              ) : (
                data.map((item, index) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/PlantSafetyRegulation/${item.id}`)}
                  >
                    <td className="px-4 py-3 text-gray-500 border text-center">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>

                    <td className="px-4 py-3 text-black-700 font-medium border">{item.refNo}</td>

                    <td className="px-4 py-3 border font-medium">
                      {item?.createdDate ? new Date(item.createdDate).toLocaleDateString('en-GB') : '-'}
                    </td>

                    <td className="px-4 py-3 border font-medium">{item.department}</td>

                    <td className="px-4 py-3 border font-medium">{item.priority}</td>

                    <td className="px-4 py-3 border font-medium">{item.location}</td>

                    <td className={`px-4 py-3 border ${item.statusColor}`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${getDotColor(item.status)}`} />
                        {item.status}
                      </div>
                    </td>

                    <td className="px-4 py-3 border">
                      <div className="flex gap-3" onClick={(e) => e.stopPropagation()}>
                        {canEditOrDelete(item) ? (
                          <>
                            <button className="text-red-500" onClick={() => handleDeleteClick(item.id)}>
                              <Trash2 size={18} />
                            </button>

                            <button className="text-blue-500" onClick={() => onEdit?.(item.id)}>
                              <Pencil size={18} />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 relative">
            <button onClick={handleCancelDelete} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>

            <p className="text-center text-gray-800 mb-6">Are you sure you want to delete this work log?</p>

            <div className="flex justify-center gap-4">
              <button onClick={handleCancelDelete} disabled={deleteLoading} className="px-5 py-2 border rounded-md disabled:opacity-50">
                Cancel
              </button>

              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="px-5 py-2 bg-red-500 text-white rounded-md flex items-center gap-2 disabled:opacity-70"
              >
                {deleteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-500 text-white rounded-lg shadow-lg w-80 p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={20} />
            <span className="font-semibold text-base">Successfully Deleted</span>
            <button onClick={() => setShowSuccessToast(false)} className="ml-auto text-white hover:text-green-100">
              <X size={16} />
            </button>
          </div>
          <p className="text-sm text-green-100 ml-7">The record has been successfully deleted.</p>
          <button
            onClick={() => setShowSuccessToast(false)}
            className="mt-3 w-full bg-white text-green-600 font-medium py-1.5 rounded-md text-sm hover:bg-green-50"
          >
            OK
          </button>
        </div>
      )}
    </>
  );
});

PlantRegPermitTable.displayName = 'PlantRegPermitTable';

export default PlantRegPermitTable;
